import { AiBoxer, type AiSnapshot } from '../ai/boxer';
import { AdaptiveDifficulty, difficultyById, type DifficultyProfile } from '../ai/difficulty';
import { personalityById, type Personality } from '../ai/personality';
import { Bus } from '../core/bus';
import { clamp, clamp01, stdev } from '../core/math';
import { Ring } from '../core/ring';
import type { PoseFrame } from '../pose/types';
import { Recogniser, type ComboEvent, type DefenseEvent, type FootworkEvent, type PunchEvent } from '../recog';
import { Scorer, type RoundResult } from '../score/scoring';
import { ExchangeDirector, type DodgeGrade } from './exchange';
import { resolveIncoming, resolvePlayerPunch, type DefenderState, type RangeContext } from './hit';
import {
  DEFAULT_MATCH,
  type HitResult,
  type IncomingAttack,
  type MatchConfig,
  type Openness,
  type PlayerSimState,
} from './types';

export type Phase = 'idle' | 'calibrating' | 'ready' | 'fighting' | 'rest' | 'finished';

export interface Feedback {
  t: number;
  who: 'player' | 'ai';
  outcome: HitResult['outcome'];
  quality: number;
  label: string;
  detail: string;
}

export interface MatchEvents {
  feedback: Feedback;
  'round-start': { round: number };
  'round-end': RoundResult;
  'match-end': { rounds: RoundResult[]; cards: { player: number; ai: number } };
  knockdown: { who: 'player' | 'ai' };
  telegraph: IncomingAttack;
  /** A dodge was graded. Carries the player's reaction time for this attack. */
  dodge: DodgeGrade;
  /** The punish window opened or closed. */
  window: { open: boolean; ms: number; reason: Openness['reason'] };
}

/**
 * The match simulation. Owns the authoritative game state and is the only place
 * that knows about *both* the player and the AI at once.
 *
 * Ordering within a tick matters and is deliberate:
 *   1. advance timers
 *   2. resolve any incoming attack whose impact instant has arrived
 *      (BEFORE the AI decides, so the AI's next decision sees the outcome)
 *   3. update the AI
 *   4. sample continuous scoring channels
 * Player punches are resolved out-of-band, the moment the recogniser emits them,
 * because their timing comes from the camera rather than from the sim clock.
 */
export class Match {
  readonly bus = new Bus<MatchEvents>();
  readonly recogniser = new Recogniser();
  readonly scorer = new Scorer();
  readonly adaptive = new AdaptiveDifficulty(true);
  readonly exchange = new ExchangeDirector();

  ai: AiBoxer;
  /**
   * The unmodified profile the player chose. Adaptive difficulty is always
   * derived from THIS, never from the currently-applied profile — otherwise each
   * tick would adapt the already-adapted values and the bias would compound into
   * an unplayable opponent within a couple of seconds.
   */
  private baseDifficulty: DifficultyProfile;
  phase: Phase = 'idle';
  round = 1;
  roundTimeMs = 0;
  restTimeMs = 0;
  config: MatchConfig = { ...DEFAULT_MATCH };
  results: RoundResult[] = [];
  cards = { player: 0, ai: 0 };

  player: PlayerSimState = {
    ring: { x: 0, z: 0 },
    stance: 'orthodox',
    vitals: { poise: 100, stamina: 100, downs: 0 },
    guard: { left: 1, right: 1 },
    shell: 1,
  };

  /** Player's effective reach, learned from their own punches, sw. */
  playerReach = 1.35;
  private reachSamples = new Ring<number>(24);
  private gapHistory = new Ring<number>(45);
  private lastFrame: PoseFrame | null = null;
  private incoming: IncomingAttack[] = [];
  private gapAtCommit = new Map<number, number>();

  constructor(personalityId = 'metronome', difficultyId = 'medium', seed = 20260803) {
    this.baseDifficulty = difficultyById(difficultyId);
    this.ai = new AiBoxer(personalityById(personalityId), this.baseDifficulty, seed);
    this.ai.onAttack = (a) => this.onAiAttack(a);
    this.wireRecogniser();
  }

  // ------------------------------------------------------------------ wiring

  private wireRecogniser(): void {
    const r = this.recogniser;
    r.bus.on('punch-start', ({ side, t }) => {
      this.ai.blackboard.push({ kind: 'punch-start', t, side }, t);
    });
    r.bus.on('punch', (p) => this.onPlayerPunch(p));
    r.bus.on('defense', (d) => this.onPlayerDefense(d));
    r.bus.on('footwork', (f) => this.onPlayerFootwork(f));
    r.bus.on('combo', (c) => this.onPlayerCombo(c));
  }

  setOpponent(personality: Personality, difficulty: DifficultyProfile): void {
    this.baseDifficulty = difficulty;
    this.ai.setPersonality(personality);
    this.ai.setDifficulty(difficulty);
  }

  // --------------------------------------------------------------- lifecycle

  startMatch(now: number): void {
    this.round = 1;
    this.results = [];
    this.cards = { player: 0, ai: 0 };
    this.scorer.resetAll();
    this.ai.reset();
    this.adaptive.reset();
    this.player.vitals = { poise: 100, stamina: 100, downs: 0 };
    this.startRound(now);
  }

  private startRound(now: number): void {
    this.phase = 'fighting';
    this.roundTimeMs = this.config.roundSeconds * 1000;
    this.incoming.length = 0;
    this.exchange.reset();
    this.scorer.startRound(now);
    this.bus.emit('round-start', { round: this.round });
  }

  private endRound(): void {
    const res = this.scorer.finishRound(this.round);
    this.results.push(res);
    this.cards.player += res.cards.player;
    this.cards.ai += res.cards.ai;
    this.bus.emit('round-end', res);

    if (this.round >= this.config.rounds) {
      this.phase = 'finished';
      this.bus.emit('match-end', { rounds: this.results, cards: this.cards });
    } else {
      this.phase = 'rest';
      this.restTimeMs = this.config.restSeconds * 1000;
    }
  }

  nextRound(now: number): void {
    this.round++;
    // Recovery between rounds is partial: fatigue is meant to accumulate across a
    // session so the later rounds test a different skill (composure) than the first.
    this.player.vitals.stamina = Math.min(100, this.player.vitals.stamina + 45);
    this.player.vitals.poise = Math.min(100, this.player.vitals.poise + 60);
    this.ai.vitals.stamina = Math.min(100, this.ai.vitals.stamina + 50);
    this.ai.vitals.poise = Math.min(100, this.ai.vitals.poise + 65);
    this.startRound(now);
  }

  // ------------------------------------------------------------------ inputs

  /** Called from the vision pipeline whenever a new pose frame is ready. */
  onPoseFrame(f: PoseFrame): void {
    this.lastFrame = f;
    this.player.stance = f.stance;
    this.player.ring.x = clamp(f.ring.x, -2, 2);
    this.player.ring.z = clamp(f.ring.z, -1.5, 1.5);
    this.gapHistory.push(this.gap);

    if (this.phase === 'fighting') {
      this.recogniser.update(f);
      const g = this.recogniser.guard;
      this.player.guard.left = g.left;
      this.player.guard.right = g.right;
      this.player.shell = g.shell;
      this.ai.blackboard.pushGuard(g, this.gap, this.lateral, f.t);
    }
  }

  // ------------------------------------------------------------------ ticking

  update(dtMs: number, now: number): void {
    if (this.phase === 'rest') {
      this.restTimeMs -= dtMs;
      if (this.restTimeMs <= 0) this.nextRound(now);
      return;
    }
    if (this.phase !== 'fighting') return;

    this.roundTimeMs -= dtMs;

    const wasOpen = this.exchange.isOpen;
    const hitsBefore = this.exchange.hitsThisWindow;
    this.exchange.update(dtMs);
    if (wasOpen && !this.exchange.isOpen) {
      // Earning an opening and not taking it is the defining mistake of this
      // mode, so it is scored explicitly rather than just being an absence.
      if (hitsBefore === 0) this.scorer.onWindowWasted();
      this.bus.emit('window', { open: false, ms: 0, reason: 'none' });
    }

    this.resolveDueAttacks(now);
    this.ai.update(dtMs, {
      now,
      gap: this.gap,
      lateral: this.lateral,
      mode: this.config.mode,
      open: this.exchange.isOpen,
    });

    // The punish window IS the opponent's guard dropping. Overriding the AI's
    // own guard values here means the window is simultaneously the hit-resolution
    // rule, the AI's behaviour, and the thing the player literally sees on
    // screen — one concept, one source of truth, no chance of them disagreeing.
    if (this.config.mode === 'punch-out') {
      this.ai.guard.left = this.exchange.guardFor(this.ai.guard.left);
      this.ai.guard.right = this.exchange.guardFor(this.ai.guard.right);
    }

    this.tickPlayerVitals(dtMs);

    const guardMean = (this.player.guard.left + this.player.guard.right) * 0.5;
    if (this.config.mode === 'free') this.scorer.sample(dtMs, guardMean, this.gap, this.playerReach, this.aiReach);
    else this.scorer.sampleGuard(dtMs, guardMean);
    this.scorer.tickWorkRate(now);
    this.adaptive.observe(this.scorer.differential(), dtMs);
    this.ai.setDifficulty(this.adaptive.apply(this.baseDifficulty));

    if (this.roundTimeMs <= 0) this.endRound();
  }

  private tickPlayerVitals(dtMs: number): void {
    const s = dtMs / 1000;
    // Player stamina is driven by *observed* output and movement, so it is an
    // honest reflection of how hard they are actually working — which is the
    // whole point of a fitness-adjacent trainer.
    const f = this.lastFrame;
    const effort = f ? clamp01((f.arms.left.speed + f.arms.right.speed) / 9) + clamp01(Math.hypot(f.ringVel.x, f.ringVel.z) / 2.5) * 0.5 : 0;
    this.player.vitals.stamina = clamp(this.player.vitals.stamina + (2.4 - effort * 7) * s, 0, 100);
    this.player.vitals.poise = clamp(this.player.vitals.poise + 2.4 * s, 0, 100);
  }

  // --------------------------------------------------------------- resolution

  private onPlayerPunch(p: PunchEvent): void {
    if (this.phase !== 'fighting') return;

    // The AI perceives the punch on a delay; its opponent model only ever
    // learns from what it actually saw.
    this.ai.blackboard.push({ kind: 'punch', t: p.tPeak, punch: p }, p.tPeak);

    this.reachSamples.push(p.reach);
    if (this.reachSamples.size >= 5) {
      const xs = this.reachSamples.toArray().sort((a, b) => a - b);
      // 75th percentile: the reach they *can* produce, not the average of lazy shots.
      this.playerReach = clamp(xs[Math.floor(xs.length * 0.75)], 0.9, 1.9);
    }

    const ctx = this.rangeContext();
    const def = this.aiDefenderState(p.lateral);
    const res = resolvePlayerPunch(p, ctx, def);

    // What counts as a counter: in punch-out it is a punch landed inside the
    // window you earned by dodging — the thing the mode is actually teaching.
    // In free-spar it is a punch landed while the AI is committed to its own.
    const counter =
      this.config.mode === 'punch-out'
        ? this.exchange.isOpen
        : this.ai.state === 'recover' || this.ai.state === 'strike';
    this.scorer.onPlayerPunch(p, res, counter, p.tPeak);

    if (res.outcome === 'clean' || res.outcome === 'glancing') {
      const inWindow = this.exchange.registerLanded();
      if (inWindow > 0) this.scorer.onWindowHit(inWindow);
      this.ai.registerHit(res.quality, res.outcome === 'clean');
      if (this.ai.vitals.poise <= 0 && this.config.knockdowns) {
        this.ai.vitals.poise = 55;
        this.ai.vitals.downs++;
        this.bus.emit('knockdown', { who: 'ai' });
      }
    }

    this.bus.emit('feedback', {
      t: p.tPeak,
      who: 'player',
      outcome: res.outcome,
      quality: res.quality,
      label: labelFor(res, counter),
      detail: explain(res, p),
    });
  }

  private onPlayerDefense(d: DefenseEvent): void {
    this.scorer.onDefense(d);
    this.ai.blackboard.push({ kind: 'defense', t: d.tPeak, ev: d }, d.tPeak);
  }

  private onPlayerFootwork(f: FootworkEvent): void {
    this.ai.blackboard.push({ kind: 'footwork', t: f.tEnd, ev: f }, f.tEnd);
  }

  private onPlayerCombo(c: ComboEvent): void {
    this.scorer.onCombo(c);
  }

  private onAiAttack(a: IncomingAttack): void {
    this.incoming.push(a);
    this.gapAtCommit.set(a.id, this.gap);
    this.bus.emit('telegraph', a);
  }

  private resolveDueAttacks(now: number): void {
    for (let i = this.incoming.length - 1; i >= 0; i--) {
      const a = this.incoming[i];
      if (a.resolved || now < a.tImpact) continue;
      a.resolved = true;
      this.incoming.splice(i, 1);
      this.gapAtCommit.delete(a.id);

      const guard = this.recogniser.guard;
      const res = resolveIncoming(a, guard, this.rangeContext(), this.player.vitals.stamina);
      this.scorer.onIncoming(res, now);

      // Grade the defence and, if it succeeded, open the punish window. This is
      // the beat the entire mode is built around: read -> move -> capitalise.
      const grade = this.exchange.grade(a, res, guard.lastEvasionStartT, guard.lastEvasionPeakT);
      if (grade) {
        this.exchange.openWindow(grade, this.baseDifficulty.windowScale);
        this.scorer.onDodge(grade);
        this.bus.emit('dodge', grade);
        this.bus.emit('window', {
          open: true,
          ms: this.exchange.openness.remainingMs,
          reason: this.exchange.openness.reason,
        });
      }

      if (res.outcome === 'clean' || res.outcome === 'glancing') {
        const dmg = res.quality * a.power * (res.outcome === 'clean' ? 8.5 : 3.2);
        this.player.vitals.poise = Math.max(0, this.player.vitals.poise - dmg);
        this.ai.registerLanded(res.quality);
        if (this.player.vitals.poise <= 0 && this.config.knockdowns) {
          this.player.vitals.poise = 55;
          this.player.vitals.downs++;
          this.bus.emit('knockdown', { who: 'player' });
        }
      }

      this.bus.emit('feedback', {
        t: now,
        who: 'ai',
        outcome: res.outcome,
        quality: res.quality,
        label: incomingLabel(res),
        detail: res.defeatedBy ? `stopped by ${res.defeatedBy}` : '',
      });
    }
  }

  // -------------------------------------------------------------------- state

  get gap(): number {
    // Punch-out: locked. Depth from a single camera was the least reliable number
    // in the system and is now simply not part of the game.
    if (this.config.mode === 'punch-out') return this.config.neutralGap;
    return clamp(this.config.neutralGap - this.player.ring.z - this.ai.ring.z, 0.25, 3);
  }

  get lateral(): number {
    return this.ai.ring.x - this.player.ring.x;
  }

  /** Whether the HUD should name the required dodge (bottom two tiers only). */
  get showDodgePrompt(): boolean {
    return this.config.mode === 'punch-out' && this.baseDifficulty.showDodgePrompt;
  }

  get aiReach(): number {
    return 1.3 * (0.9 + 0.1 * clamp01(this.ai.vitals.stamina / 100));
  }

  /** How steady our monocular depth estimate has been, 0..1. */
  private get depthStability(): number {
    if (this.gapHistory.size < 8) return 0.4;
    const s = stdev(this.gapHistory.toArray());
    return clamp01(1 - s / 0.35);
  }

  private rangeContext(): RangeContext {
    const q = this.lastFrame?.quality ?? 0.5;
    const aiHeadDrop =
      this.ai.posture === 'duck' ? -0.55 : this.ai.posture === 'roll' ? -0.32 : this.ai.posture === 'pull' ? -0.05 : 0;
    const self = this;
    return {
      mode: this.config.mode,
      gap: this.gap,
      lateral: this.lateral,
      targetHeadHeight: 0.28 + aiHeadDrop,
      poseConfidence: q,
      depthStability: this.depthStability,
      gapDelta(a: IncomingAttack) {
        const at = self.gapAtCommit.get(a.id);
        return at === undefined ? 0 : at - self.gap;
      },
    };
  }

  /**
   * The AI's defensive posture as the hit resolver sees it. `punchLateral` picks
   * which glove is on the line: a shot travelling to the player's right arrives on
   * the opponent's left side, since the two fighters face each other.
   */
  private aiDefenderState(punchLateral: number): DefenderState {
    const p = this.ai.posture;
    const evading =
      p === 'slip-left' ? 'slip' : p === 'slip-right' ? 'slip' : p === 'roll' ? 'roll' : p === 'duck' ? 'duck' : p === 'pull' ? 'pull' : null;
    const headOffX = p === 'slip-left' ? -0.32 : p === 'slip-right' ? 0.32 : p === 'roll' ? -0.24 : 0;
    const headOffY = p === 'duck' ? -0.5 : p === 'roll' ? -0.3 : 0;
    const headOffZ = p === 'pull' ? -0.28 : 0;
    const guardOnLine = punchLateral > 0 ? this.ai.guard.left : this.ai.guard.right;
    return {
      guardOnLine,
      activelyBlocking: this.ai.state === 'block',
      shell: this.ai.posture === 'guard-tight' ? 1 : 0.6,
      evading,
      evasionDepth: evading ? 0.8 : 0,
      headOffX,
      headOffY,
      headOffZ,
      committed: this.ai.state === 'windup' || this.ai.state === 'strike',
      stamina: this.ai.vitals.stamina,
    };
  }

  aiSnapshot(): AiSnapshot {
    return this.ai.snapshot();
  }
}

function labelFor(r: HitResult, counter: boolean): string {
  if (r.outcome === 'clean') return counter ? 'COUNTER!' : r.quality > 0.85 ? 'FLUSH' : 'CLEAN';
  if (r.outcome === 'glancing') return 'GLANCING';
  if (r.outcome === 'blocked') return 'BLOCKED';
  if (r.outcome === 'slipped') return 'SLIPPED';
  return 'MISS';
}

function incomingLabel(r: HitResult): string {
  switch (r.outcome) {
    case 'clean':
      return 'HIT';
    case 'glancing':
      return 'GRAZED';
    case 'blocked':
      return 'BLOCKED';
    case 'slipped':
      return 'EVADED!';
    default:
      return 'MISSED YOU';
  }
}

function explain(r: HitResult, p: PunchEvent): string {
  const t = r.terms;
  if (r.outcome === 'miss') {
    if (t.range < 0.3) return 'short — step in';
    if (t.lateral < 0.3) return 'off line';
    if (t.height < 0.3) return 'wrong level';
    return 'nothing there';
  }
  if (r.outcome === 'blocked') return 'their guard caught it';
  if (r.outcome === 'slipped') return `they ${r.defeatedBy === 'slip' ? 'slipped' : (r.defeatedBy ?? 'moved')}`;
  return `${p.type} · ${Math.round(r.quality * 100)}%`;
}
